import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../api/orders';
import { customersAPI } from '../../api/customers';
import { paymentsAPI } from '../../api/payments';
import { subscriptionsAPI } from '../../api/subscriptions';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import './DashboardManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TIME_PERIODS = [
  { id: 'today',      label: 'Today',      icon: '📅' },
  { id: 'week',       label: 'This Week',  icon: '📆' },
  { id: 'month',      label: 'This Month', icon: '🗓️' },
  { id: 'year',       label: 'This Year',  icon: '📊' },
];

// ─── Inline Components ────────────────────────────────────────────────────────
const MetricCard = ({ icon, label, value, sub, accent }) => (
  <div className="db-metric-card" style={{ borderTopColor: accent }}>
    <div className="db-metric-icon" style={{ background: `${accent}15`, color: accent }}>{icon}</div>
    <div className="db-metric-content">
      <div className="db-metric-label">{label}</div>
      <div className="db-metric-value" style={{ color: accent }}>{value}</div>
      {sub && <div className="db-metric-sub">{sub}</div>}
    </div>
  </div>
);

const SectionHeader = ({ icon, title, action }) => (
  <div className="db-section-header">
    <div className="db-section-title">
      <span className="db-section-icon">{icon}</span>
      {title}
    </div>
    {action}
  </div>
);

const StatBox = ({ label, value, icon, color }) => (
  <div className="db-stat-box">
    <div className="db-stat-icon" style={{ background: `${color}15`, color }}>{icon}</div>
    <div>
      <div className="db-stat-value">{value}</div>
      <div className="db-stat-label">{label}</div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [ordersSummary, setOrdersSummary] = useState(null);
  const [customersSummary, setCustomersSummary] = useState(null);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [subscriptionsSummary, setSubscriptionsSummary] = useState(null);

  // ── Load dashboard data ─────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [orders, customers, payments, subscriptions] = await Promise.all([
        ordersAPI.getDashboardSummary().catch(() => null),
        customersAPI.getDashboardSummary().catch(() => null),
        paymentsAPI.getDashboardSummary().catch(() => null),
        subscriptionsAPI.getDashboardSummary().catch(() => null),
      ]);

      setOrdersSummary(orders?.data || null);
      setCustomersSummary(customers?.data || null);
      setPaymentsSummary(payments?.data || null);
      setSubscriptionsSummary(subscriptions?.data || null);

    } catch (err) {
      console.error('Dashboard load error:', err);
      showNotification('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading && !ordersSummary) {
    return (
      <div className="ps-root">
        <div className="ps-loading">Loading dashboard…</div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Dashboard</h1>
          <p className="ps-subtitle">System performance overview and key metrics</p>
        </div>
        <Button variant="secondary" onClick={loadDashboard}>↺ Refresh</Button>
      </div>

      {/* ── Time Period Selector ── */}
      <div className="db-period-selector">
        <div className="db-period-tabs">
          {TIME_PERIODS.map(p => (
            <button
              key={p.id}
              className={`db-period-tab${period === p.id ? ' active' : ''}`}
              onClick={() => setPeriod(p.id)}
            >
              <span>{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          KEY METRICS ROW
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="db-metrics-row">
        <MetricCard
          icon="💰" label="Total Revenue" accent="#10b981"
          value={formatCurrency(paymentsSummary?.totalAmount || 0)}
          sub={`${paymentsSummary?.paidCount || 0} paid transactions`}
        />
        <MetricCard
          icon="📦" label="Total Orders" accent="#3b82f6"
          value={ordersSummary?.totalCount || 0}
          sub={`${ordersSummary?.pendingCount || 0} pending`}
        />
        <MetricCard
          icon="👥" label="Active Customers" accent="#8b5cf6"
          value={customersSummary?.activeCount || 0}
          sub={`${customersSummary?.totalCount || 0} total`}
        />
        <MetricCard
          icon="⭐" label="Active Subscriptions" accent="#f59e0b"
          value={subscriptionsSummary?.activeCount || 0}
          sub={formatCurrency(subscriptionsSummary?.totalRevenue || 0)}
        />
        <MetricCard
          icon="📊" label="Today's Revenue" accent="#06b6d4"
          value={formatCurrency(paymentsSummary?.todayRevenue || 0)}
          sub={`${paymentsSummary?.todayCount || 0} payments`}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ORDERS OVERVIEW
          ══════════════════════════════════════════════════════════════════════ */}
      {ordersSummary && (
        <div className="db-section">
          <SectionHeader 
            icon="📦" 
            title="Orders Overview" 
            action={<Button variant="secondary" size="small" onClick={() => navigate('/orders')}>View All →</Button>}
          />
          <div className="db-stats-grid">
            <StatBox label="Pending" value={ordersSummary.pendingCount || 0} icon="⏳" color="#f59e0b" />
            <StatBox label="In Progress" value={ordersSummary.inProgressCount || 0} icon="⚙️" color="#3b82f6" />
            <StatBox label="Ready" value={ordersSummary.readyCount || 0} icon="✅" color="#10b981" />
            <StatBox label="Completed" value={ordersSummary.completedCount || 0} icon="🎉" color="#8b5cf6" />
            <StatBox label="Cancelled" value={ordersSummary.cancelledCount || 0} icon="❌" color="#ef4444" />
            <StatBox label="Total Revenue" value={formatCurrency(ordersSummary.totalRevenue || 0)} icon="💰" color="#10b981" />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PAYMENTS OVERVIEW
          ══════════════════════════════════════════════════════════════════════ */}
      {paymentsSummary && (
        <div className="db-section">
          <SectionHeader 
            icon="💳" 
            title="Payments Overview" 
            action={<Button variant="secondary" size="small" onClick={() => navigate('/payments')}>View All →</Button>}
          />
          <div className="db-cards-row">
            
            {/* Payment Stats */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title">Payment Statistics</h3>
              </div>
              <div className="db-card-content">
                <div className="db-info-grid">
                  <div className="db-info-item">
                    <span className="db-info-label">Total Payments</span>
                    <span className="db-info-value">{paymentsSummary.totalCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Paid</span>
                    <span className="db-info-value" style={{ color: '#10b981' }}>{paymentsSummary.paidCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Pending</span>
                    <span className="db-info-value" style={{ color: '#f59e0b' }}>{paymentsSummary.pendingCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Refunded</span>
                    <span className="db-info-value" style={{ color: '#ef4444' }}>{paymentsSummary.refundedCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Total Amount</span>
                    <span className="db-info-value">{formatCurrency(paymentsSummary.totalAmount || 0)}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Avg Payment</span>
                    <span className="db-info-value">{formatCurrency(paymentsSummary.avgPayment || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Today */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title">Today's Revenue</h3>
              </div>
              <div className="db-card-content">
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#10b981', marginBottom: 8 }}>
                    {formatCurrency(paymentsSummary.todayRevenue || 0)}
                  </div>
                  <div style={{ fontSize: 14, color: '#64748b' }}>
                    {paymentsSummary.todayCount || 0} transaction{(paymentsSummary.todayCount || 0) !== 1 ? 's' : ''} today
                  </div>
                </div>
                <div className="db-breakdown-divider" />
                <div className="db-breakdown-item">
                  <span className="db-breakdown-label">Total Refunded</span>
                  <span className="db-breakdown-value" style={{ color: '#ef4444' }}>
                    {formatCurrency(paymentsSummary.totalRefunded || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          CUSTOMERS OVERVIEW
          ══════════════════════════════════════════════════════════════════════ */}
      {customersSummary && (
        <div className="db-section">
          <SectionHeader 
            icon="👥" 
            title="Customers Overview" 
            action={<Button variant="secondary" size="small" onClick={() => navigate('/customers')}>View All →</Button>}
          />
          <div className="db-cards-row">
            
            {/* Customer Stats */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title">Customer Statistics</h3>
              </div>
              <div className="db-card-content">
                <div className="db-info-grid">
                  <div className="db-info-item">
                    <span className="db-info-label">Total Customers</span>
                    <span className="db-info-value">{customersSummary.totalCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Active</span>
                    <span className="db-info-value" style={{ color: '#10b981' }}>{customersSummary.activeCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">With Wallet</span>
                    <span className="db-info-value" style={{ color: '#8b5cf6' }}>{customersSummary.withWalletCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">With Credit</span>
                    <span className="db-info-value" style={{ color: '#f59e0b' }}>{customersSummary.withCreditCount || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Total Wallet</span>
                    <span className="db-info-value">{formatCurrency(customersSummary.totalWalletBalance || 0)}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Credit Outstanding</span>
                    <span className="db-info-value" style={{ color: '#ef4444' }}>
                      {formatCurrency(customersSummary.totalCreditUsed || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Activity */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title">Customer Activity</h3>
              </div>
              <div className="db-card-content">
                <div className="db-info-grid">
                  <div className="db-info-item">
                    <span className="db-info-label">Total Spent</span>
                    <span className="db-info-value">{formatCurrency(customersSummary.totalSpent || 0)}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Total Orders</span>
                    <span className="db-info-value">{customersSummary.totalOrders || 0}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Avg Orders</span>
                    <span className="db-info-value">{(customersSummary.avgOrdersPerCustomer || 0).toFixed(1)}</span>
                  </div>
                  <div className="db-info-item">
                    <span className="db-info-label">Loyalty Points</span>
                    <span className="db-info-value" style={{ color: '#f59e0b' }}>
                      {customersSummary.totalLoyaltyPoints || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SUBSCRIPTIONS OVERVIEW
          ══════════════════════════════════════════════════════════════════════ */}
      {subscriptionsSummary && (
        <div className="db-section">
          <SectionHeader 
            icon="⭐" 
            title="Subscriptions Overview" 
            action={<Button variant="secondary" size="small" onClick={() => navigate('/subscriptions')}>View All →</Button>}
          />
          <div className="db-stats-grid">
            <StatBox 
              label="Total Subscriptions" 
              value={subscriptionsSummary.totalCount || 0} 
              icon="📋" 
              color="#64748b" 
            />
            <StatBox 
              label="Active" 
              value={subscriptionsSummary.activeCount || 0} 
              icon="✅" 
              color="#10b981" 
            />
            <StatBox 
              label="Expiring Soon" 
              value={subscriptionsSummary.expiringSoonCount || 0} 
              icon="⏰" 
              color="#f59e0b" 
            />
            <StatBox 
              label="Expired" 
              value={subscriptionsSummary.expiredCount || 0} 
              icon="⏳" 
              color="#ef4444" 
            />
            <StatBox
              label="Lifetime Revenue"
              value={formatCurrency(subscriptionsSummary.totalRevenue || 0)}
              icon="💰"
              color="#10b981"
            />
            <StatBox
              label="Outstanding Credit"
              value={formatCurrency(subscriptionsSummary.outstandingCredit || 0)}
              icon="🪙"
              color="#6366f1"
            />
            <StatBox
              label="Subscribed Customers"
              value={subscriptionsSummary.activeCustomersCount || 0}
              icon="👥"
              color="#8b5cf6"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardManagement;
